import { useId, useState } from 'react';
import { Box, Button, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { subscriptionFeatureStyles } from './SubscriptionFeature.styles';

interface FeatureContent {
  description: string;
  image?: string;
  animated?: boolean;
  fullWidth?: boolean;
}

const featureContent = new Map<string, FeatureContent>([
  ['curriculum_reports_placeholder', {
    description: 'Teacher Reports let you see what you have taught to this class, and averages for completion, engagement and quiz scores.',
  }],
  ['student_facing_feedback', {
    description: 'Student Feedback lets students know whether their responses are correct or incorrect in real time or after an assignment ends.',
  }],
  ['ui_curriculum_show_dashboard_responses', {
    description: 'See which students are struggling, which questions your class is having difficulty with, class averages and student quiz scores - all in real time.',
    image: '/resources/response-dashboard-popup.gif',
    animated: true,
  }],
  ['ui_curriculum_show_experience_card_standards', {
    description: 'See which learning standards (and breakouts) are taught in the learning experience, and track student work against them.',
    image: '/resources/teks_standards.png',
  }],
  ['ui_curriculum_reactivate_experiences', {
    description: 'Did this experience end before all the students finished? Now you can reactivate an Ended Experience to allow students to continue.',
    image: '/resources/reactivate.gif',
    animated: true,
    fullWidth: true,
  }],
  ['add_multi_class_experience', {
    description: 'Would you like to assign multiple classes at once? Now you can!',
  }],
]);

export interface SubscriptionFeatureProps {
  feature?: string;
}

export const SubscriptionFeature = ({ feature }: SubscriptionFeatureProps = {}) => {
  const theme = useTheme();
  const headingId = useId();
  const exampleId = useId();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [showExample, setShowExample] = useState<boolean | null>(null);
  const content = featureContent.get(feature);
  const imageVisible = !content?.animated || (showExample ?? !prefersReducedMotion);

  return (
    <Box component="section" aria-labelledby={headingId} sx={subscriptionFeatureStyles.container(theme)}>
      <Box sx={subscriptionFeatureStyles.header}>
        <Box component="svg" viewBox="0 0 100 100" aria-hidden="true" focusable="false" sx={subscriptionFeatureStyles.icon}>
          <use href="/themes/exploros/icons/icons.svg#subscription-icon" />
        </Box>
        <Typography component="h2" id={headingId} sx={subscriptionFeatureStyles.heading(theme)}>
          Subscription Feature
        </Typography>
      </Box>
      <Box sx={subscriptionFeatureStyles.content}>
        {content && (
          <Typography component="p" sx={subscriptionFeatureStyles.description}>
            {content.description}
          </Typography>
        )}
        {content?.image && (
          <Box sx={subscriptionFeatureStyles.example}>
            <Box id={exampleId} hidden={!imageVisible}>
              {imageVisible && (
                <Box
                  component="img"
                  src={content.image}
                  alt=""
                  sx={[subscriptionFeatureStyles.image, content.fullWidth ? { width: '100%' } : {}]}
                />
              )}
            </Box>
            {content.animated && (
              <Button
                size="small"
                aria-expanded={imageVisible}
                aria-controls={exampleId}
                onClick={() => setShowExample(!imageVisible)}
                sx={subscriptionFeatureStyles.action(theme)}
              >
                {imageVisible ? 'Hide example' : 'Show example'}
              </Button>
            )}
          </Box>
        )}
        <Typography component="p" sx={subscriptionFeatureStyles.profileGuidance}>
          If your school already has a trial or subscription, make sure your school is listed in your profile to gain full program access.
        </Typography>
        <Link
          href="https://exploros.com/subscription/#subscription"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={[subscriptionFeatureStyles.link, subscriptionFeatureStyles.action(theme)]}
        >
          Would you like the full program? Click here to learn more about subscribing.
        </Link>
      </Box>
    </Box>
  );
};

export default SubscriptionFeature;
